/**
 * MainMenu.ts
 * Full-screen title screen shown on game boot. The game world renders
 * in the background — clicking PLAY hides this overlay and starts play.
 */

import { Container, Graphics, Text, TextStyle, Ticker } from 'pixi.js';
import { APP_RELEASE_STAMP } from '../appMeta';

// ── Colours ────────────────────────────────────────────────────────────────
const BG_COL    = 0x141820;
const ACCENT    = 0x00ffcc;
const BTN_BG    = 0x0d1f1a;
const BTN_HOVER = 0x152d27;
const BTN_BDR   = 0x2a5a4a;

export class MainMenu {
  private readonly container = new Container();
  private screenW = 1280;
  private screenH = 720;
  private beaconTick: ((delta: number) => void) | null = null;
  private beaconElapsed = 0;

  constructor(
    ui: Container,
    private readonly onPlay:     () => void,
    private readonly onContinue: (() => void) | null = null,
  ) {
    ui.addChild(this.container);
  }

  show(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    this.rebuild();
    this.container.visible = true;
  }

  hide(): void {
    this.container.visible = false;
    this.stopBeaconTick();
  }

  private stopBeaconTick(): void {
    if (this.beaconTick) {
      Ticker.shared.remove(this.beaconTick);
      this.beaconTick = null;
    }
  }

  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    if (this.container.visible) this.rebuild();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private rebuild(): void {
    this.stopBeaconTick();
    this.container.removeChildren();

    const W = this.screenW, H = this.screenH;
    const cx = W / 2, cy = H / 2;

    // ── Background ───────────────────────────────────────────────────────────
    const bg = new Graphics();
    bg.beginFill(BG_COL, 0.93);
    bg.drawRect(0, 0, W, H);
    bg.endFill();
    this.container.addChild(bg);

    // ── Title — "FUTURE ◉ BEACON" lockup ────────────────────────────────────
    // Scale font size down on narrow screens
    const titleFS = W < 500 ? Math.max(22, Math.floor(W / 17)) : 44;
    const titleLS = titleFS < 32 ? 3 : 6;
    const titleStyle = new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   titleFS,
      fontWeight: 'bold',
      fill:       '#c9fff8',
      letterSpacing: titleLS,
    });
    const word1 = new Text('FUTURE', titleStyle);
    const word2 = new Text('BEACON', titleStyle);

    // Beacon glyph slot — wide enough for the glow, proportional to font size
    const beaconScale = Math.min(1.3, Math.max(0.65, titleFS / 36));
    const slotW       = Math.round(titleFS * 1.1);
    const totalW      = word1.width + slotW + word2.width;
    const lockLeft    = cx - totalW / 2;
    const titleY      = cy - 120;

    word1.anchor.set(0, 0.5);
    word1.position.set(lockLeft, titleY);
    this.container.addChild(word1);

    word2.anchor.set(0, 0.5);
    word2.position.set(lockLeft + word1.width + slotW, titleY);
    this.container.addChild(word2);

    // Accent lines flanking the full lockup
    this.line(lockLeft - 24, titleY, lockLeft - 10, titleY);
    this.line(lockLeft + totalW + 10, titleY, lockLeft + totalW + 24, titleY);

    // Animated beacon glyph centred in the slot between the two words
    const beaconGfx = new Graphics();
    beaconGfx.position.set(Math.round(lockLeft + word1.width + slotW / 2), titleY);
    this.container.addChild(beaconGfx);

    this.beaconElapsed = 0;
    this.beaconTick = (delta: number) => {
      this.beaconElapsed += delta / 60;
      const pulse = 0.5 + 0.5 * Math.sin(this.beaconElapsed * 2.4);
      const s     = beaconScale;
      beaconGfx.clear();
      beaconGfx.beginFill(0x37e0d0, 0.10 + 0.18 * pulse);
      beaconGfx.drawCircle(0, 0, (13 + pulse * 6) * s);
      beaconGfx.endFill();
      beaconGfx.beginFill(0x6ff0e4, 0.22 + 0.20 * pulse);
      beaconGfx.drawCircle(0, 0, 7 * s);
      beaconGfx.endFill();
      beaconGfx.beginFill(0xc9fff8, 0.92);
      beaconGfx.drawCircle(0, 0, (3 + pulse * 1.2) * s);
      beaconGfx.endFill();
    };
    Ticker.shared.add(this.beaconTick);

    // ── Tagline ──────────────────────────────────────────────────────────────
    const tagFS = W < 500 ? 10 : 12;
    const tagLS = W < 500 ? 2  : 4;
    const tagText = W < 420
      ? 'PROTECT THE SIGNAL · SAVE THE SCENE'   // shorter on tiny screens
      : 'PROTECT THE SIGNAL  ·  SAVE THE SCENE';
    const tag = new Text(tagText, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   tagFS,
      fill:       '#7ab8aa',
      letterSpacing: tagLS,
      wordWrap:   true,
      wordWrapWidth: W - 32,
      align:      'center',
    }));
    tag.anchor.set(0.5, 0.5);
    tag.position.set(cx, cy - 82);
    this.container.addChild(tag);

    // Thin rule below tagline
    this.rule(cx, cy - 64, 200);

    // ── Buttons ──────────────────────────────────────────────────────────────
    // Cap button width on narrow screens
    const btnW = Math.min(220, W - 80);
    if (this.onContinue) {
      this.addBtn(cx, cy - 34, '▶   PLAY',      ACCENT,   false, this.onPlay,     btnW);
      this.addBtn(cx, cy + 30, '↑   LOAD SAVE', 0x4a9a8a, false, this.onContinue, btnW);
    } else {
      this.addBtn(cx, cy - 10, '▶   PLAY', ACCENT, false, this.onPlay, btnW);
    }

    // ── Controls hint ────────────────────────────────────────────────────────
    // On mobile (touch screens) keyboard shortcuts aren't relevant — show
    // touch-friendly hints instead, or hide on very narrow screens.
    const hintStyle = new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   10,
      fill:       '#628e82',
      letterSpacing: 1,
      wordWrap:   true,
      wordWrapWidth: W - 48,
      align:      'center',
    });
    const hints = W < 480
      ? [
          'tap — select · drag — move order',
          'pinch / scroll — zoom',
        ]
      : [
          'left-click / drag  —  select units',
          'right-click  —  move order',
          'WASD / arrows  —  pan camera    scroll  —  zoom',
          'ESC  —  pause        X/G/C/F  —  unit commands',
        ];
    for (let i = 0; i < hints.length; i++) {
      const h = new Text(hints[i], hintStyle);
      h.anchor.set(0.5, 0);
      h.position.set(cx, cy + 60 + i * 15);
      this.container.addChild(h);
    }

    // ── Version stamp ────────────────────────────────────────────────────────
    const ver = new Text(APP_RELEASE_STAMP, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   10,
      fill:       '#507a70',
    }));
    ver.anchor.set(1, 1);
    ver.position.set(W - 16, H - 12);
    this.container.addChild(ver);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  private addBtn(
    cx: number, cy: number,
    label: string,
    colour: number,
    disabled: boolean,
    cb: () => void,
    btnW = 220,
  ): void {
    const BW = btnW, BH = 48;

    const g = new Graphics();

    const draw = (hover: boolean) => {
      g.clear();
      g.beginFill(hover && !disabled ? BTN_HOVER : BTN_BG, 0.95);
      g.lineStyle(
        1,
        hover && !disabled ? colour : BTN_BDR,
        hover && !disabled ? 0.9 : disabled ? 0.2 : 0.55,
      );
      g.drawRoundedRect(0, 0, BW, BH, 3);
      g.endFill();
      g.lineStyle(0);
    };
    draw(false);
    g.position.set(cx - BW / 2, cy - BH / 2);
    g.alpha = disabled ? 0.4 : 1;

    if (!disabled) {
      g.eventMode = 'static';
      g.cursor    = 'pointer';
      g.on('pointerover',  () => draw(true));
      g.on('pointerout',   () => draw(false));
      g.on('pointerdown',  cb);
    }

    this.container.addChild(g);

    const txt = new Text(label, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   16,
      fontWeight: 'bold',
      fill:       disabled ? '#2a4040' : `#${colour.toString(16).padStart(6, '0')}`,
      letterSpacing: 3,
    }));
    txt.anchor.set(0.5, 0.5);
    txt.position.set(cx, cy);
    txt.eventMode = 'none';
    this.container.addChild(txt);
  }

  private line(x1: number, y: number, x2: number, _y2: number): void {
    const g = new Graphics();
    g.lineStyle(1, ACCENT, 0.35);
    g.moveTo(x1, y);
    g.lineTo(x2, y);
    g.lineStyle(0);
    this.container.addChild(g);
  }

  private rule(cx: number, y: number, halfW: number): void {
    const g = new Graphics();
    g.lineStyle(1, ACCENT, 0.2);
    g.moveTo(cx - halfW, y);
    g.lineTo(cx + halfW, y);
    g.lineStyle(0);
    this.container.addChild(g);
  }
}
